"use client";

import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Utensils } from "lucide-react";
import { InertiaProps, SeccionSlider } from "@/types/app";
import { usePage } from "@inertiajs/react";

export default function CategoriaNavegacion({
    data,
}: {
    data: SeccionSlider[];
}) {
    const scrollRef = useRef<HTMLDivElement>(null);

    const [shouldCenter, setShouldCenter] = useState(false);

    // Obtenemos los menus reales con subcategorías
    const { navbar, navPersonalizados } = usePage<InertiaProps>().props;

    const getSubcategorias = (nombreCategoria: string) => {
        const navItem = navbar?.find(
            (item) => item.seccion.nombre.toLowerCase() === nombreCategoria.toLowerCase()
        );
        if (!navItem) return [];
        return navItem.seccion.id === 3 ? navPersonalizados : navItem.subSeccion;
    };

    const getUrlSubcategoria = (categoriaNombre: string, subCategoriaNombre: string, isPersonalizada: boolean) => {
        if (isPersonalizada) {
            return `/producto/personalizado/${subCategoriaNombre}`;
        }
        return `/tienda/${categoriaNombre}/${subCategoriaNombre}`;
    };

    const checkShouldCenter = () => {
        const isMobile = window.innerWidth < 768;
        const cardWidth = isMobile ? 260 : 300;
        const gap = isMobile ? 24 : 24;
        const containerPadding = isMobile ? 32 : 32;
        const availableWidth = window.innerWidth - containerPadding;

        const itemsPerView = Math.floor(
            (availableWidth + gap) / (cardWidth + gap)
        );
        setShouldCenter(data.length <= itemsPerView);
    };

    useEffect(() => {
        checkShouldCenter();
        window.addEventListener("resize", checkShouldCenter);
        return () => window.removeEventListener("resize", checkShouldCenter);
    }, [data.length]);

    const scroll = (direction: "left" | "right") => {
        if (scrollRef.current) {
            const isMobile = window.innerWidth < 768;
            const scrollAmount = isMobile ? 284 : 324;
            scrollRef.current.scrollBy({
                left: direction === "left" ? -scrollAmount : scrollAmount,
                behavior: "smooth",
            });
        }
    };

    return (
        <section className="relative py-8 md:py-12 bg-white flex justify-center w-full">
            <div className="w-full max-w-none px-4 md:px-6 lg:px-8">
                <div className="flex flex-col items-center text-center mb-10">
                    <div className="flex items-center gap-3 mb-4">
                        <Utensils className="w-6 h-6 md:w-8 md:h-8 text-[#790546]" />
                        <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight text-balance">
                            Nuestras Especialidades
                        </h2>
                    </div>
                    <p className="text-gray-600 max-w-2xl text-sm md:text-base leading-relaxed px-6 text-balance">
                        Descubre la variedad de productos artesanales que tenemos para ti
                    </p>
                    <div className="w-16 h-0.5 bg-[#790546] rounded-full mt-5 mb-0"></div>
                </div>

                <div className="relative w-full">
                    <button
                        onClick={() => scroll("left")}
                        className="absolute left-2 md:-left-5 top-1/2 -translate-y-1/2 z-30 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-[#790546] p-2.5 hover:p-3 rounded-full hover:shadow-lg transition-all duration-300 flex items-center justify-center opacity-100 shadow-md"
                        aria-label="Anterior"
                    >
                        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 hover:w-6 hover:h-6 transition-all duration-300" />
                    </button>

                    <button
                        onClick={() => scroll("right")}
                        className="absolute right-2 md:-right-5 top-1/2 -translate-y-1/2 z-30 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-[#790546] p-2.5 hover:p-3 rounded-full hover:shadow-lg transition-all duration-300 flex items-center justify-center opacity-100 shadow-md"
                        aria-label="Siguiente"
                    >
                        <ChevronRight className="w-5 h-5 md:w-6 md:h-6 hover:w-6 hover:h-6 transition-all duration-300" />
                    </button>

                    <div
                        ref={scrollRef}
                        className={`flex gap-5 md:gap-6 overflow-x-auto scrollbar-hide pb-6 md:pb-4 pt-2 scroll-smooth px-6 md:px-4 w-full ${shouldCenter ? "justify-center" : ""}`}
                        style={{
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        {data?.map((categoria) => {
                            if (!categoria || !categoria.nombre) return null;

                            const subCats = getSubcategorias(categoria.nombre);
                            const isPersonalizada = categoria.nombre.toLowerCase().includes("personalizada");

                            return (
                                <div key={categoria.id} className="flex-shrink-0 min-w-[260px] max-w-[260px] md:min-w-[300px] md:max-w-[300px] bg-white rounded-xl border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-300 cursor-pointer flex flex-col group">
                                    <a href={categoria.link} className="block relative h-40 md:h-48 overflow-hidden rounded-t-xl">
                                        <img
                                            src={categoria.icono || "/placeholder.svg"}
                                            alt={categoria.nombre}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                    </a>

                                    <div className="p-4 md:p-5 text-center flex flex-col flex-grow justify-between">
                                        <div className="w-full mb-3 flex flex-col items-center">
                                            <a href={categoria.link} className="mb-2 block w-full">
                                                <h3 className="text-base md:text-lg font-semibold text-[#790546] text-balance min-h-[1.5rem] flex items-center justify-center">
                                                    {categoria.nombre}
                                                </h3>
                                            </a>

                                            {/* Botones Dinámicos de Enlaces */}
                                            {subCats && subCats.length > 0 && (
                                                <div className="flex flex-wrap justify-center gap-1.5 mb-2 relative z-20 min-h-[46px] content-start">
                                                    {subCats.slice(0, 5).map((sub: any) => (
                                                        <a
                                                            key={sub?.id}
                                                            href={getUrlSubcategoria(categoria.nombre, sub?.nombre || "", isPersonalizada)}
                                                            className="text-[10px] md:text-[11px] font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 px-2 py-0.5 rounded-full transition-colors border border-transparent hover:border-gray-300"
                                                        >
                                                            {sub?.nombre}
                                                        </a>
                                                    ))}
                                                    {subCats.length > 5 && (
                                                        <a
                                                            href={categoria.link}
                                                            className="text-[10px] md:text-[11px] font-medium text-[#790546] bg-[#790546]/10 hover:bg-[#790546]/20 px-2 py-0.5 rounded-full transition-colors border border-transparent hover:border-[#790546]/30"
                                                        >
                                                            +{subCats.length - 5} Más
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                window.location.href = categoria.link;
                                            }}
                                            className="w-full bg-[#790546] text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-300 text-sm shadow-sm group-hover:shadow-md group-hover:bg-[#5a0333] mt-auto active:scale-[0.98]"
                                        >
                                            Ver Productos
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
